<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import DataTable from '@/components/DataTable.vue'
  import BookmarkInput from '@/components/BookmarkInput.vue'
  import { ref, inject, onMounted, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { use36AgreementStore } from '@/stores/article36AgreementStore'
  import { useRegionStore } from '@/stores/regionStore'
  import { useEgovApplicationStore } from '@/stores/egovApplicationStore'
  import { useContractStore } from '@/stores/contractStore'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const agreementStore = use36AgreementStore()
  const regionStore = useRegionStore()
  const egovStore = useEgovApplicationStore()
  const contractStore = useContractStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '36協定' }]

  // リファレンス変数
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  const searchText = ref('')
  const searchKey = 'status'
  const searchSelect = ref('')
  const searchKey2 = 'egovStatus'
  const searchSelect2 = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')
  const searchColor = ref('')

  const showPopupEGOV = ref(false)
  // 選択されたアイテムを保持
  const selectedItem = ref(null)
  const egovType = ref('')

  // 基本のe-Gov状況リスト
  const baseEgovStatusList = ['到達', '審査開始', '審査終了', '手続終了', '申請取下げ']

  // 動的なe-Gov状況リスト
  const dynamicEgovStatusList = computed(() => {
    const statuses = new Set(baseEgovStatusList)
    agreementStore.agreements.forEach(item => {
      if (item.egovStatus) {
        statuses.add(item.egovStatus)
      }
    })
    return Array.from(statuses)
  })

  // 会社選択肢
  const companyOptions = computed(() => {
    return agreementStore.companyNameOptions || []
  })

  // 事業場選択肢
  const branchOptions = computed(() => {
    return agreementStore.branchNameOptions || []
  })

  // 管轄選択肢
  const bureauOptions = computed(() => {
    return regionStore.bureauOptions || []
  })

  // 監督署選択肢
  const officeOptions = computed(() => {
    return regionStore.officeOptions(agreementStore.formData?.laborBureauCode) || []
  })

  // e-Gov申請種別リスト
  const egovProcList = computed(() => {
    return egovStore.procList || []
  })

  // methods /////////////////////////////////////

  onMounted(async () => {
    // ステータスで絞り込み
    if (route.query.status) {
      searchSelect.value = agreementStore.getStatusValueFromLabel(route.query.status)
    }

    // テーブル定義を初期化
    agreementStore.initTableList()

    // e-Govデータを取得
    await egovStore.getEgovMetadata()

    // 地域データを取得
    await regionStore.getPrefectures()
    await regionStore.getLaborData()

    // 会社名リストを取得
    await agreementStore.getCompanyNames()
    // 36協定一覧を取得
    await agreementStore.getAgreements()

    if (route.query.action === 'new') {
      openEditDialog()
    }
  })

  // 保存
  const onSave = async () => {
    const message = await agreementStore.saveAgreement()

    if (message) {
      await dialogStore.showMessageDialog('保存', message)
      showPopupEdit.value = false
    }
  }

  // コピー
  const onCopy = () => {
    agreementStore.copyAgreement()
    isEdit.value = false
  }

  // 削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', 'この36協定データを削除してもよろしいですか？')) {
      const message = await agreementStore.deleteAgreement()

      if (message) {
        await dialogStore.showMessageDialog('削除', message)
        showPopupEdit.value = false
      }
    }
  }

  // 編集
  const openEditDialog = async item => {
    isEdit.value = item ? true : false
    await agreementStore.setFormData(item)
    showPopupEdit.value = true
  }

  // 会社変更時の処理
  const onCompanyChange = async () => {
    await agreementStore.onCompanyChange()
  }

  // 事業場変更時の処理
  const onBranchChange = async event => {
    agreementStore.isDeletedBranch = false // 事業場が削除された場合のフラグをリセット
    await agreementStore.onBranchChange()
  }

  // 管轄変更時の処理
  const onLaborBureauChange = () => {
    if (agreementStore.formData) {
      agreementStore.formData.laborOfficeCode = ''
    }
  }

  // 日付変更時の処理
  const onDateChange = (type, event) => {
    agreementStore.updateDateInfo(type, event.target.value)
  }

  // e-Gov申請ダイアログを開く
  const openEgovDialog = async item => {
    // 契約有効チェック
    if (!(await contractStore.getIsActive())) {
      dialogStore.showAlertDialog('エラー', '有料プランの契約が必要です')
      return
    }
    // 選択した36協定の会社、事業場どちらかがnullの場合はエラーを表示
    if (!item.companyId || !item.branchId) {
      dialogStore.showAlertDialog('エラー', '会社または事業場が選択されていません')
      return
    }
    selectedItem.value = item // 選択されたアイテムを保存
    egovType.value = '' // 手続き種別をリセット
    showPopupEGOV.value = true
  }

  // e-Gov申請処理
  const createEgovApplication = () => {
    if (selectedItem.value && egovType.value) {
      router.push({
        name: 'egovapplication',
        query: {
          egovId: selectedItem.value.egovId || 'new',
          procId: egovType.value,
          branchId: selectedItem.value.branchId,
          agreementId: selectedItem.value.id,
        },
      })
    }
    showPopupEGOV.value = false
  }

  // Rowアクションのハンドラー
  const handleRowAction = ({ table, item }) => {
    if (table.key === 'egovStatus') {
      openEgovDialog(item) // itemを渡す
    }
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>36協定</h2>
      <button type="button" class="new" @click="openEditDialog()">新規登録</button>
      <div class="filters">
        <div class="area">
          <BookmarkInput v-model="searchColor" />
        </div>
        <div class="select">
          状況:
          <select v-model="searchSelect">
            <option value="">-</option>
            <option v-for="item in agreementStore.statusOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <!-- <div class="select">
          e-Gov状況:
          <select v-model="searchSelect2">
            <option value="">-</option>
            <option v-for="item in dynamicEgovStatusList" :key="item" :value="item">{{ item }}</option>
          </select>
        </div> -->
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>
        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>
    <div class="main-body">
      <DataTable :table-list="agreementStore.tableList" :data-list="agreementStore.agreements" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditDialog" @length="length = $event" :search-key="searchKey" :search-select="searchSelect" :search-key2="searchKey2" :search-select2="searchSelect2" :search-list2="dynamicEgovStatusList" :search-color="searchColor" @row-action="handleRowAction" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <EditDialog v-model:visible="showPopupEdit" :show-delete-button="isEdit" :show-copy-button="isEdit" :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>{{ isEdit ? '編集' : '新規登録' }}</template>
    <template v-slot:select>
      <div class="select">
        <select v-model="agreementStore.formData.status">
          <option></option>
          <option v-for="item in agreementStore.statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </option>
        </select>
      </div>
    </template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <!-- 会社選択部分の修正 -->
          <dt>会社</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.companyId" />
            <div class="select">
              <select v-model="agreementStore.formData.companyId" @change="onCompanyChange">
                <!-- 削除された会社の場合 -->
                <option v-if="agreementStore.getCompanySelectState(isEdit) === 'deleted'" value="null" disabled>削除された会社</option>
                <!-- 通常選択時の表示 -->
                <option v-else-if="agreementStore.getCompanySelectState(isEdit) === 'select'" value="null" disabled>会社を選択</option>
                <option v-else value="null" disabled>会社を選択</option>
                <option v-for="company in companyOptions" :key="company.value" :value="company.value">
                  {{ company.label }}
                </option>
              </select>
            </div>
          </dd>

          <!-- 事業場選択部分の修正 -->
          <dt>事業場</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.branchId" />
            <div class="select">
              <select v-model="agreementStore.formData.branchId" @change="onBranchChange" :disabled="!agreementStore.formData.companyId">
                <!-- 削除された事業場の場合 -->
                <option v-if="agreementStore.getBranchSelectState(isEdit) === 'deleted'" :value="agreementStore.formData.branchId" disabled>削除された事業場</option>
                <!-- 事業場選択を促す場合 -->
                <option v-else-if="agreementStore.getBranchSelectState(isEdit) === 'select'" value="null" disabled>事業場を選択</option>
                <!-- 通常の無効状態の場合 -->
                <option v-else value="null" disabled>事業場を選択</option>
                <!-- 選択肢リスト -->
                <option v-for="branch in branchOptions" :key="branch.value" :value="branch.value">
                  {{ branch.label }}
                </option>
              </select>
            </div>
          </dd>
          <dt>特別条項</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.specialClause" />
            <input type="text" v-model="agreementStore.formData.specialClause" />
          </dd>
          <dt>終了予定日</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.scheduledEndDate" />
            <input type="date" v-model="agreementStore.formData.scheduledEndDate" />
          </dd>
          <dt>
            <label>
              <input type="checkbox" v-model="agreementStore.formData.dateJson.endDate.isChecked" @change="agreementStore.updateCheckboxState('endDate')" />
              終了日
            </label>
          </dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.endDate" />
            <input type="date" v-model="agreementStore.formData.dateJson.endDate.date" @change="onDateChange('endDate', $event)" :disabled="!agreementStore.formData.dateJson.endDate.isChecked" />
            <div class="date-user" v-if="agreementStore.formData.dateJson.endDate?.userName">
              {{ agreementStore.getCheckInfo('endDate') }}
            </div>
          </dd>
          <dt>管轄</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.laborBureauCode" />
            <div class="select">
              <select v-model="agreementStore.formData.laborBureauCode" @change="onLaborBureauChange">
                <option value="">選択してください</option>
                <option v-for="bureau in bureauOptions" :key="bureau.code" :value="bureau.code">
                  {{ bureau.name }}
                </option>
              </select>
            </div>
          </dd>
          <dt>監督署</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.laborOfficeCode" />
            <div class="select">
              <select v-model="agreementStore.formData.laborOfficeCode" :disabled="!agreementStore.formData.laborBureauCode">
                <option value="">選択してください</option>
                <option v-for="office in officeOptions" :key="office.code" :value="office.code">
                  {{ office.name }}
                </option>
              </select>
            </div>
          </dd>
          <dt>備考</dt>
          <dd>
            <BookmarkInput v-model="agreementStore.formData.colorInfo.memo" />
            <input type="text" v-model="agreementStore.formData.memo" />
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <EditDialog v-model:visible="showPopupEGOV" save-button-text="申請書を作成" :show-delete-button="false" :show-copy-button="false" @save="createEgovApplication" :disabled-save-button="egovType === ''">
    <template v-slot:title>e-Gov電子申請</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>会社名</dt>
          <dd>{{ selectedItem?.companyName || '-' }}</dd>
          <dt>事業場名</dt>
          <dd>{{ selectedItem?.branchName || '-' }}</dd>
          <dt>種別</dt>
          <dd>
            <div class="select">
              <select v-model="egovType">
                <option></option>
                <option v-for="egov in egovProcList" :key="egov.code" :value="egov.code">{{ egov.name }}</option>
              </select>
            </div>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .date-user {
    color: #6c757d;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    text-align: right;
    margin-left: 10px;
  }
</style>
